import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import { isDesignerRole } from '../utils/userRoles';
import AppHeader from '../components/ui/AppHeader';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import EngineerHeader from '../components/pm/EngineerHeader';
import ShopScheduleTimeline from '../components/pm/ShopScheduleTimeline';
import GanttTimeline from '../components/pm/GanttTimeline';
import { getShopScheduleViewModel } from '../utils/shopScheduleView';
import { scopeProductionScheduleToSupplier } from '../utils/supplierProductionScope';
import {
  applySupplierPortalPreviewToSearchParams,
  parseSupplierPortalPreviewRole,
} from '../utils/vendorPortalPreviewQuery';
import { buildGroupTimelineView, projectColorForId } from '../utils/groupTimeline';
import {
  buildGroupTimelineSupplierIndex,
  filterTasksVisibleToSupplierOrg,
} from '../utils/groupTimelineSupplier';
import type { ProjectTask, RfqSubmission, TaskGroup, TimberProject } from '../types';
import { ArrowLeft, AlertTriangle, Eye, Loader2, Monitor, X } from 'lucide-react';
import '../components/pm/pm-shared.css';
import './ProductionSchedulePage.css';

const ROW_H = 44;
const CELL = 18;
const LABEL_W = 260;

/** Keeps a timeline crash from blanking the whole app; shows the error inline instead. */
class TimelineErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="ps-warn-banner" role="alert">
          <AlertTriangle size={16} aria-hidden />
          <span>Timeline failed to render: {this.state.error.message}</span>
        </div>
      );
    }
    return this.props.children;
  }
}

type SupplierScheduleResponse = {
  projects: Array<{ id: string; dealId: string; name: string; location?: string }>;
  groups: Array<{ id: string; projectId: string; name: string; color: string; position: number }>;
  tasks: Array<{
    id: string;
    projectId: string;
    groupId: string;
    parentTaskId?: string;
    title: string;
    department: string;
    status: string;
    priority: string;
    progress: number;
    position: number;
    dependencies: string[];
    startDate: string;
    endDate: string;
    supplierOrgIds?: string[];
    supplierManual?: boolean;
    structureId?: string;
    structureName?: string;
    structureIds?: string[];
    structureNames?: string[];
  }>;
};

type SupplierScheduleData = {
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
};

function mapSupplierScheduleResponse(data: SupplierScheduleResponse): SupplierScheduleData {
  const projects = (data.projects ?? []).map(
    p =>
      ({
        id: p.id,
        dealId: p.dealId,
        name: p.name,
        location: p.location ?? '',
        clientEmail: '',
        clientName: '',
        status: 'production',
        frameType: 'custom',
        startDate: new Date(),
        targetEndDate: new Date(),
        value: 0,
        projectType: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      }) as TimberProject,
  );
  const groups: TaskGroup[] = (data.groups ?? []).map(g => ({
    id: g.id,
    projectId: g.projectId,
    name: g.name,
    color: g.color,
    position: g.position,
  }));
  const tasks = (data.tasks ?? []).map(
    t =>
      ({
        id: t.id,
        projectId: t.projectId,
        groupId: t.groupId,
        parentTaskId: t.parentTaskId,
        title: t.title,
        department: t.department,
        assignedTo: [],
        status: t.status,
        priority: t.priority,
        startDate: new Date(t.startDate),
        endDate: new Date(t.endDate),
        dependencies: t.dependencies ?? [],
        progress: t.progress ?? 0,
        position: t.position ?? 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        ...(t.supplierOrgIds?.length ? { supplierOrgIds: t.supplierOrgIds } : {}),
        ...(t.supplierManual ? { supplierManual: true } : {}),
        ...(t.structureId ? { structureId: t.structureId } : {}),
        ...(t.structureName ? { structureName: t.structureName } : {}),
        ...(t.structureIds?.length ? { structureIds: t.structureIds } : {}),
        ...(t.structureNames?.length ? { structureNames: t.structureNames } : {}),
      }) as ProjectTask,
  );
  return { projects, groups, tasks };
}

const ProductionSchedulePage: React.FC = () => {
  const { user } = useAuth();
  const {
    timberProjects,
    projectTasks,
    taskGroups,
    projectOnboardings,
    rfqProjects,
    rfqPackages,
    rfqSubmissions,
    supplierOrgs,
  } = useCRM();
  useCRMCollections(['projectTasks', 'taskGroups', 'projectOnboardings']);

  const [onlyOverlaps, setOnlyOverlaps] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const isSupplier = user?.role === 'supplier';
  const canPreview = user?.role === 'employee' || user?.role === 'admin';

  // ── Supplier accounts: rules block timberProjects/projectTasks reads, so fetch the org-scoped schedule from the callable.
  const [supplierData, setSupplierData] = useState<SupplierScheduleData | null>(null);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupplier) return;
    let cancelled = false;
    setSupplierLoading(true);
    setSupplierError(null);
    const fn = httpsCallable<Record<string, never>, SupplierScheduleResponse>(
      getFunctions(undefined, 'us-central1'),
      'supplierProductionSchedule',
    );
    fn({})
      .then(res => {
        if (cancelled) return;
        setSupplierData(mapSupplierScheduleResponse(res.data));
      })
      .catch(err => {
        if (cancelled) return;
        setSupplierError(
          err instanceof Error ? err.message : 'Could not load your production schedule.',
        );
      })
      .finally(() => {
        if (!cancelled) setSupplierLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSupplier]);

  // ── Staff preview: view the schedule as a supplier org would see it.
  // Driven by `?viewAs=` so the preview survives navigation from the previewed supplier header.
  const previewOrgId = canPreview ? searchParams.get('viewAs')?.trim() || null : null;
  const previewRole = parseSupplierPortalPreviewRole(searchParams.get('as'));
  const setPreviewOrgId = (orgId: string | null) => {
    const next = applySupplierPortalPreviewToSearchParams(searchParams, orgId, previewRole);
    setSearchParams(next, { replace: true });
  };
  const previewOrg = previewOrgId ? supplierOrgs.find(o => o.id === previewOrgId) ?? null : null;

  const submissionsByPackage = useMemo(() => {
    if (!previewOrgId) return new Map<string, RfqSubmission[]>();
    const map = new Map<string, RfqSubmission[]>();
    rfqSubmissions.forEach(s => {
      const list = map.get(s.rfqPackageId) || [];
      list.push(s);
      map.set(s.rfqPackageId, list);
    });
    return map;
  }, [previewOrgId, rfqSubmissions]);

  const supplierView = isSupplier || !!previewOrgId;

  // ── Supplier / preview data: awarded projects with their Production task groups.
  const scopedData: SupplierScheduleData | null = useMemo(() => {
    if (isSupplier) {
      return supplierData ?? { projects: [], groups: [], tasks: [] };
    }
    if (previewOrgId) {
      const scoped = scopeProductionScheduleToSupplier({
        orgId: previewOrgId,
        rfqProjects,
        rfqPackages,
        submissionsByPackage,
        timberProjects,
        projectTasks,
        taskGroups,
        projectOnboardings,
      });
      return { projects: scoped.projects, groups: scoped.groups, tasks: scoped.tasks };
    }
    return null;
  }, [
    isSupplier,
    supplierData,
    previewOrgId,
    rfqProjects,
    rfqPackages,
    submissionsByPackage,
    timberProjects,
    projectTasks,
    taskGroups,
    projectOnboardings,
  ]);

  // Full Production group, minus rows assigned to other suppliers.
  const hubTimelineView = useMemo(() => {
    if (!scopedData) return null;
    const viewerOrgId = previewOrgId || (isSupplier ? user?.supplierOrgId?.trim() || null : null);
    const index = viewerOrgId
      ? buildGroupTimelineSupplierIndex({
          family: 'production',
          projects: scopedData.projects,
          tasks: scopedData.tasks,
          rfqProjects,
          rfqPackages,
          rfqSubmissions,
          supplierOrgs,
        })
      : null;
    const tasks = viewerOrgId && index
      ? filterTasksVisibleToSupplierOrg({
          tasks: scopedData.tasks,
          byTaskId: index.byTaskId,
          viewerOrgId,
          projects: scopedData.projects,
          rfqProjects,
          rfqPackages,
        })
      : scopedData.tasks;
    const extraTaskIds = tasks.map(t => t.id).filter((id): id is string => !!id);
    return buildGroupTimelineView({
      family: 'production',
      projects: scopedData.projects,
      groups: scopedData.groups,
      tasks,
      layout: 'project',
      sortMode: 'date',
      extraTaskIds,
    });
  }, [
    scopedData,
    previewOrgId,
    isSupplier,
    user?.supplierOrgId,
    rfqProjects,
    rfqPackages,
    rfqSubmissions,
    supplierOrgs,
  ]);

  const noopUpdateTask = useCallback(async (_id: string, _u: Partial<ProjectTask>) => {}, []);

  // Staff (non-preview) shop schedule keeps the department-based view.
  const model = useMemo(
    () => getShopScheduleViewModel(supplierView ? [] : projectTasks, supplierView ? [] : timberProjects),
    [supplierView, projectTasks, timberProjects],
  );

  const hubHeader =
    isDesignerRole(user?.role) ? (
      <ArchitectHeader />
    ) : user?.role === 'engineer' ? (
      <EngineerHeader />
    ) : (
      <AppHeader
        supplierNavPreviewOrgId={previewOrgId}
        supplierNavPreviewRole={previewOrgId ? previewRole : null}
      />
    );

  return (
    <div className="ps-page">
      {hubHeader}
      <div className="ps-main">
        {previewOrg && (
          <div className="ps-preview-banner" role="status">
            <Eye size={14} aria-hidden />
            <span>
              Previewing as <strong>{previewOrg.name || previewOrgId}</strong> — production
              schedule limited to their awarded projects.
            </span>
            <button
              type="button"
              className="ps-preview-banner-exit"
              onClick={() => setPreviewOrgId(null)}
            >
              <X size={13} aria-hidden /> Exit preview
            </button>
          </div>
        )}

        <div className="ps-toolbar">
          {!supplierView && (
            <Link to="/project-hub" className="ps-back">
              <ArrowLeft size={16} /> Project hub
            </Link>
          )}
          <div className="ps-toolbar-text">
            <h1 className="ps-title">{supplierView ? 'Project Hub' : 'Shop schedule'}</h1>
            <p className="ps-sub">
              {supplierView
                ? 'Production timeline for your awarded projects.'
                : 'Production and installation tasks across projects. Overlap highlights possible CNC conflicts.'}
            </p>
          </div>
          <div className="ps-toolbar-actions">
            {!supplierView && (
              <>
                <Link
                  to="/shop-schedule/display"
                  className="ps-toolbar-display-link"
                  title="Full-screen display for TV or monitor"
                >
                  <Monitor size={16} aria-hidden />
                  Big screen
                </Link>
                <label className="ps-filter">
                  <input
                    type="checkbox"
                    checked={onlyOverlaps}
                    onChange={(e) => setOnlyOverlaps(e.target.checked)}
                  />
                  Only overlapping
                </label>
              </>
            )}
          </div>
        </div>

        {!supplierView && model.overlapPairs.length > 0 && (
          <div className="ps-warn-banner" role="status">
            <AlertTriangle size={16} aria-hidden />
            <span>
              {model.overlapPairs.length} overlapping task pair{model.overlapPairs.length === 1 ? '' : 's'} — review CNC / shop capacity.
            </span>
          </div>
        )}

        {isSupplier && supplierLoading ? (
          <div className="ps-supplier-status">
            <Loader2 size={16} className="ps-spin" aria-hidden /> Loading your production schedule…
          </div>
        ) : isSupplier && supplierError ? (
          <div className="ps-warn-banner" role="alert">
            <AlertTriangle size={16} aria-hidden />
            <span>{supplierError}</span>
          </div>
        ) : supplierView && (hubTimelineView?.tasks.length ?? 0) === 0 ? (
          <div className="ps-supplier-status">
            No production scheduled for {previewOrg ? 'their' : 'your'} projects yet.
          </div>
        ) : supplierView && hubTimelineView ? (
          <div className="ps-hub-timeline">
            <TimelineErrorBoundary>
              <GanttTimeline
                tasks={hubTimelineView.tasks}
                groups={hubTimelineView.groups}
                milestones={[]}
                teamMembers={[]}
                onUpdateTask={noopUpdateTask}
                onSelectTask={() => {}}
                displayMode
                initialZoom="fit"
                showCriticalPathOverlay={false}
                resolveTaskBarColor={task => projectColorForId(task.projectId)}
              />
            </TimelineErrorBoundary>
          </div>
        ) : (
          <ShopScheduleTimeline
            model={model}
            onlyOverlaps={onlyOverlaps}
            cellWidth={CELL}
            rowHeight={ROW_H}
            labelWidth={LABEL_W}
            displayMode={false}
          />
        )}
      </div>
    </div>
  );
};

export default ProductionSchedulePage;
